import React, { useState, useEffect } from 'react';
import {
  Bot, MessageSquare, Lightbulb, BookOpen, Send, Paperclip, Mic, Zap,
  Settings, History, ThumbsUp, ThumbsDown, Copy, Share2, MoreHorizontal,
  TrendingUp, TrendingDown, Activity, Target, Award, Users, Clock,
  CheckCircle, AlertTriangle, Eye, Download, BarChart3, FileText,
  Save, Edit, Trash2, Search, Filter, Calendar, RefreshCw, Play,
  Pause, RotateCcw, Plus, ChevronUp, ChevronDown, Maximize2, Minimize2,
  ExternalLink, Link, Star, Heart, Flag, Tag, Folder, User, Mail,
  Phone, MapPin, Globe, Key, Smartphone, EyeOff, Bell, Lock, Unlock,
  Shield, Info, HelpCircle, X, Check, Minus, PieChart, LineChart,
  BarChart, Gauge, Thermometer, Move, RotateCw, Wifi, WifiOff,
  Signal, Battery, BatteryLow, Volume2, VolumeX, Mic as MicIcon,
  MicOff, Camera, CameraOff, Video, VideoOff, Headphones,
  HeadphonesOff, Monitor, MonitorOff, Laptop, Tablet, Watch, Tv,
  Gamepad2, Mouse, Keyboard, Printer, Scanner, HardDrive, Cpu,
  MemoryStick, Database, Server, Cloud, CloudOff, Upload,
  Download as DownloadIcon, Link as LinkIcon, LinkOff, Share,
  ShareOff, Lock as LockIcon, Unlock as UnlockIcon, Key as KeyIcon,
  Fingerprint, Shield as ShieldIcon, ShieldCheck, ShieldAlert,
  AlertCircle, AlertTriangle as AlertTriangleIcon, Info as InfoIcon,
  HelpCircle as HelpCircleIcon, CheckCircle as CheckCircleIcon,
  XCircle, MinusCircle, PlusCircle, PlayCircle, PauseCircle,
  StopCircle, SkipBack, SkipForward, Rewind, FastForward,
  Repeat, Shuffle, Volume1, Battery as BatteryIcon,
  BatteryLow as BatteryLowIcon, Signal as SignalIcon,
  Wifi as WifiIcon, WifiOff as WifiOffIcon,
  Maximize2 as Maximize2Icon, Minimize2 as Minimize2Icon,
  Move as MoveIcon, RotateCw as RotateCwIcon
} from 'lucide-react';
import MockDataService from '../../../services/MockDataService';

const AgentAIAssistant = () => {
  const [aiData, setAiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('chat');
  const [currentMessage, setCurrentMessage] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);

  useEffect(() => {
    const fetchAiData = async () => {
      try {
        setLoading(true);
        const response = await MockDataService.getAgentAIAssistant('agent-001');
        if (response.success) {
          setAiData(response.data);
          setSelectedConversation(response.data.agentAIAssistant.aiChatInterface.conversations[0]);
        } else {
          setError('Failed to load AI assistant data');
        }
      } catch (err) {
        setError('An error occurred while fetching AI assistant data.');
        console.error('Error fetching AI assistant data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAiData();
  }, []);

  if (loading) {
    return <div className="p-6 text-center text-gray-500 dark:text-gray-400">Loading AI assistant...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-500 dark:text-red-400">Error: {error}</div>;
  }

  if (!aiData) {
    return <div className="p-6 text-center text-gray-500 dark:text-gray-400">No AI assistant data available.</div>;
  }

  const { agentAIAssistant, aiStats } = aiData;
  const { aiChatInterface, smartSuggestions, knowledgeRetrieval, automatedResponses, ticketAnalysis, learningAssistant, performanceInsights, integrationTools } = agentAIAssistant;

  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return 'text-green-600 dark:text-green-400';
    if (confidence >= 70) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 90) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    if (confidence >= 70) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 dark:text-red-400';
      case 'medium': return 'text-yellow-600 dark:text-yellow-400';
      case 'low': return 'text-green-600 dark:text-green-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />;
      case 'down': return <TrendingDown className="h-4 w-4 text-red-600 dark:text-red-400" />;
      case 'stable': return <Activity className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
      default: return <Activity className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
    }
  };

  const handleSendMessage = () => {
    if (currentMessage.trim()) {
      // In a real implementation, this would send the message to the AI
      //console.log('Sending message:', currentMessage);
      setCurrentMessage('');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Bot className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  AI Assistant
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Your intelligent helpdesk companion
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
                <History className="h-4 w-4 inline-block mr-2" />
                History
              </button>
              <button className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
                <Settings className="h-4 w-4 inline-block mr-2" />
                Settings
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* AI Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Conversations</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{aiStats.totalConversations}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
              </div>
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <MessageSquare className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Suggestions</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{aiStats.totalSuggestions}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Accuracy: {aiStats.suggestionAccuracy}%</p>
              </div>
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <Lightbulb className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Response Time</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{aiStats.averageResponseTime}s</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Average</p>
              </div>
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <Clock className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Satisfaction</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{aiStats.userSatisfaction}/5</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Rating</p>
              </div>
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <Star className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-8">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'chat', name: 'AI Chat', icon: MessageSquare },
                { id: 'suggestions', name: 'Smart Suggestions', icon: Lightbulb },
                { id: 'knowledge', name: 'Knowledge Retrieval', icon: BookOpen },
                { id: 'responses', name: 'Auto Responses', icon: Send },
                { id: 'analysis', name: 'Ticket Analysis', icon: BarChart3 },
                { id: 'insights', name: 'Performance Insights', icon: TrendingUp },
                { id: 'integrations', name: 'Integrations', icon: Link }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <tab.icon className="h-4 w-4 inline-block mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* AI Chat Tab */}
            {activeTab === 'chat' && (
              <div className="space-y-6">
                {/* Chat Interface */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">AI Chat Interface</h3>
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        Online
                      </span>
                      <button className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300">
                        <Settings className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Chat Messages */}
                  <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                    {selectedConversation?.messages.map((message) => (
                      <div key={message.id} className={`flex ${message.sender === 'agent' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          message.sender === 'agent' 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-600'
                        }`}>
                          <div className="text-sm">{message.content}</div>
                          <div className={`text-xs mt-1 ${
                            message.sender === 'agent' ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'
                          }`}>
                            {new Date(message.timestamp).toLocaleTimeString()}
                          </div>
                          {message.metadata?.confidence && (
                            <div className="mt-2">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getConfidenceBadge(message.metadata.confidence)}`}>
                                {message.metadata.confidence}% confidence
                              </span>
                            </div>
                          )}
                          {message.reactions && message.reactions.length > 0 && (
                            <div className="mt-2 flex space-x-1">
                              {message.reactions.map((reaction, index) => (
                                <span key={index} className="text-sm">
                                  {reaction.emoji} {reaction.count}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Message Input */}
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={currentMessage}
                        onChange={(e) => setCurrentMessage(e.target.value)}
                        placeholder="Type your message..."
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      />
                    </div>
                    <button className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300">
                      <Paperclip className="h-5 w-5" />
                    </button>
                    <button className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300">
                      <Mic className="h-5 w-5" />
                    </button>
                    <button className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300">
                      <Zap className="h-5 w-5" />
                    </button>
                    <button
                      onClick={handleSendMessage}
                      className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
                    >
                      <Send className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Quick Actions */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Quick Actions</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {aiChatInterface.quickActions.map((action) => (
                      <button
                        key={action.id}
                        className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
                      >
                        <div className="text-2xl mb-2">{action.icon}</div>
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100">{action.name}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{action.description}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Shortcut: {action.shortcut}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Smart Suggestions Tab */}
            {activeTab === 'suggestions' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Smart Suggestions</h3>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Accuracy: {smartSuggestions.learningData.learningAccuracy}%
                    </span>
                    <button className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300">
                      <RefreshCw className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {smartSuggestions.suggestions.map((suggestion) => (
                    <div key={suggestion.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{suggestion.title}</h4>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getConfidenceBadge(suggestion.confidence)}`}>
                            {suggestion.confidence}% confidence
                          </span>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPriorityBadge(suggestion.priority)}`}>
                            {suggestion.priority} priority
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{suggestion.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                          <span>Category: {suggestion.category}</span>
                          <span>Source: {suggestion.source}</span>
                          <span>Created: {new Date(suggestion.createdAt).toLocaleDateString()}</span>
                        </div>
                        <div className="flex space-x-2">
                          {suggestion.actions.map((action) => (
                            <button
                              key={action.id}
                              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                                action.type === 'apply'
                                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                                  : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              {action.title}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Knowledge Retrieval Tab */}
            {activeTab === 'knowledge' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Knowledge Retrieval</h3>
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search knowledge base..."
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                      />
                      <Search className="h-4 w-4 absolute right-3 top-3 text-gray-400" />
                    </div>
                    <button className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors">
                      Search
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {knowledgeRetrieval.searchResults.map((result) => (
                    <div key={result.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{result.title}</h4>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getConfidenceBadge(result.confidence)}`}>
                            {result.confidence}% relevance
                          </span>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {result.views} views
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{result.content}</p>
                      {result.aiSummary && (
                        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900 rounded-lg">
                          <h5 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">AI Summary:</h5>
                          <p className="text-sm text-blue-800 dark:text-blue-200">{result.aiSummary}</p>
                        </div>
                      )}
                      {result.keyPoints && result.keyPoints.length > 0 && (
                        <div className="mb-4">
                          <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Key Points:</h5>
                          <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                            {result.keyPoints.map((point, index) => (
                              <li key={index}>{point}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                          <span>Category: {result.category}</span>
                          <span>Rating: {result.rating}/5</span>
                          <span>Updated: {new Date(result.lastUpdated).toLocaleDateString()}</span>
                        </div>
                        <div className="flex space-x-2">
                          <button className="px-3 py-1 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            View Article
                          </button>
                          <button className="px-3 py-1 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors">
                            Use in Response
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Auto Responses Tab */}
            {activeTab === 'responses' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Automated Responses</h3>
                  <button className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors">
                    <Plus className="h-4 w-4 inline-block mr-2" />
                    Create Template
                  </button>
                </div>

                <div className="space-y-4">
                  {automatedResponses.responseTemplates.map((template) => (
                    <div key={template.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{template.name}</h4>
                        <div className="flex items-center space-x-2">
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {template.category}
                          </span>
                          {template.aiGenerated && (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                              AI Generated
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{template.description}</p>
                      <div className="mb-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                        <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Template Content:</h5>
                        <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{template.content}</p>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                          <span>Usage: {template.usageCount} times</span>
                          <span>Success Rate: {template.successRate}%</span>
                          <span>Created: {new Date(template.createdAt).toLocaleDateString()}</span>
                        </div>
                        <div className="flex space-x-2">
                          <button className="px-3 py-1 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            Edit
                          </button>
                          <button className="px-3 py-1 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors">
                            Use Template
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ticket Analysis Tab */}
            {activeTab === 'analysis' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Ticket Analysis</h3>
                
                <div className="space-y-4">
                  {ticketAnalysis.analysisResults.map((analysis) => (
                    <div key={analysis.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          Analysis for Ticket {analysis.ticketId}
                        </h4>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getConfidenceBadge(analysis.confidence)}`}>
                            {analysis.confidence}% confidence
                          </span>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {analysis.processingTime}ms
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Analysis Results:</h5>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Complexity:</span>
                              <span className="text-gray-900 dark:text-gray-100">{analysis.result.complexity}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Urgency:</span>
                              <span className="text-gray-900 dark:text-gray-100">{analysis.result.urgency}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Category:</span>
                              <span className="text-gray-900 dark:text-gray-100">{analysis.result.category}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Sentiment:</span>
                              <span className="text-gray-900 dark:text-gray-100">{analysis.result.sentiment}</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Keywords:</h5>
                          <div className="flex flex-wrap gap-2">
                            {analysis.result.keywords.map((keyword, index) => (
                              <span key={index} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                                {keyword}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {analysis.insights && analysis.insights.length > 0 && (
                        <div className="mb-6">
                          <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Insights:</h5>
                          <div className="space-y-2">
                            {analysis.insights.map((insight) => (
                              <div key={insight.id} className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{insight.description}</span>
                                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getConfidenceBadge(insight.confidence)}`}>
                                    {insight.confidence}% confidence
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                                  <span>Type: {insight.type}</span>
                                  <span>Impact: {insight.impact}</span>
                                  <span>Actionable: {insight.actionable ? 'Yes' : 'No'}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {analysis.recommendations && analysis.recommendations.length > 0 && (
                        <div>
                          <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Recommendations:</h5>
                          <div className="space-y-2">
                            {analysis.recommendations.map((rec) => (
                              <div key={rec.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                                <div>
                                  <h6 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{rec.title}</h6>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">{rec.description}</p>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPriorityBadge(rec.priority)}`}>
                                    {rec.priority} priority
                                  </span>
                                  <span className="text-xs text-gray-600 dark:text-gray-400">
                                    {rec.estimatedTime} min
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Performance Insights Tab */}
            {activeTab === 'insights' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Performance Insights</h3>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {performanceInsights.insights.map((insight) => (
                    <div key={insight.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{insight.title}</h4>
                        <div className="flex items-center space-x-2">
                          {getTrendIcon(insight.trend)}
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            insight.impact === 'positive' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                            insight.impact === 'negative' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                          }`}>
                            {insight.impact}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{insight.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                          {insight.value} {insight.unit}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Confidence: {insight.confidence}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Recommendations</h4>
                  <div className="space-y-4">
                    {performanceInsights.recommendations.map((rec) => (
                      <div key={rec.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h5 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{rec.title}</h5>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPriorityBadge(rec.priority)}`}>
                            {rec.priority} priority
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{rec.description}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                            <span>Impact: {rec.impact}</span>
                            <span>Effort: {rec.effort}</span>
                            <span>Timeline: {rec.timeline}</span>
                          </div>
                          <button className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors">
                            Implement
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Integrations Tab */}
            {activeTab === 'integrations' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">AI Integrations</h3>
                
                <div className="space-y-4">
                  {integrationTools.integrations.map((integration) => (
                    <div key={integration.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{integration.name}</h4>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            integration.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                            integration.status === 'inactive' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                            'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          }`}>
                            {integration.status}
                          </span>
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {integration.type}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Configuration:</h5>
                          <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                            <div>Endpoint: {integration.configuration.apiEndpoint}</div>
                            <div>Sync Frequency: {integration.configuration.syncFrequency} min</div>
                            <div>Auto Sync: {integration.configuration.autoSync ? 'Enabled' : 'Disabled'}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Status:</h5>
                          <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                            <div>Last Sync: {new Date(integration.lastSync).toLocaleString()}</div>
                            <div>Sync Status: {integration.syncStatus}</div>
                            <div>Success Rate: {integration.successRate}%</div>
                            <div>Error Count: {integration.errorCount}</div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Last updated: {new Date(integration.lastSync).toLocaleString()}
                        </div>
                        <div className="flex space-x-2">
                          <button className="px-3 py-1 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            Configure
                          </button>
                          <button className="px-3 py-1 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors">
                            Test Connection
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentAIAssistant;
